/**
 * SiestAir FR sales page (prefix sia-).
 *
 * Offer: one product with colour variants. A pack adds 1/2/3 units of the
 * chosen colour; the pack price is applied at checkout by Shopify automatic
 * discounts. The page shows variant price x quantity - the pack's
 * "Réduction" setting, which must equal that discount.
 *
 * The cart drawer is always drawn from /cart.js (never from page state), so
 * what it shows is what checkout charges. Every cart change dispatches the
 * theme's CartLinesUpdateEvent (04-traps.md D8).
 *
 * type="module": runs once per page even if several sections load it.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const dataEl = document.getElementById('sia-data');
const DATA = dataEl ? JSON.parse(dataEl.textContent) : null;

const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

let nf;
function fmt(cents) {
  if (!nf) {
    try {
      nf = new Intl.NumberFormat(document.documentElement.lang || (DATA && DATA.locale) || 'fr', { style: 'currency', currency: (DATA && DATA.currency) || 'EUR' });
    } catch (e) {
      nf = { format: (n) => n.toFixed(2).replace('.', ',') + ' €' };
    }
  }
  return nf.format((Number(cents) || 0) / 100);
}
const pad = (n) => String(n).padStart(2, '0');
function addDays(n) { const d = new Date(); d.setDate(d.getDate() + n); return d; }
function shortDate(d) { return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }); }
function fill(tpl, map) { return Object.keys(map).reduce((s, k) => s.split('[' + k + ']').join(map[k]), tpl || ''); }

/* ====================== Product state ====================== */
const state = {
  pack: DATA ? DATA.defaultPack : 0,
  variant: null,
  ship: DATA && DATA.ship ? !!DATA.ship.on : false,
  warr: DATA && DATA.warr ? !!DATA.warr.on : false,
};
if (DATA) {
  const activeSwatch = $('.sia-swatch.is-active');
  const id = activeSwatch ? Number(activeSwatch.getAttribute('data-sia-variant')) : 0;
  state.variant = DATA.variants.find((v) => v.id === id) || DATA.variants.find((v) => v.available) || DATA.variants[0];
}

function packPrice(pack, variant) { return variant.price * pack.qty - pack.reduction; }
function packCompare(pack, variant) { return variant.compare * pack.qty; }
function packForQty(qty) { return DATA.packs.find((p) => p.qty === qty) || null; }
function priceForQty(qty, variant) {
  const p = packForQty(qty);
  return p ? packPrice(p, variant) : variant.price * qty;
}
function unitLabel(qty) { return qty + ' ' + (qty > 1 ? DATA.unitWordPlural : DATA.unitWord); }

function atcTotal() {
  const pack = DATA.packs[state.pack];
  let total = packPrice(pack, state.variant);
  if (state.ship && DATA.ship) total += DATA.ship.price;
  if (state.warr && DATA.warr) total += DATA.warr.price;
  return total;
}

function renderProduct() {
  if (!DATA) return;
  const v = state.variant;
  $$('[data-sia-pack]').forEach((card) => {
    const i = Number(card.getAttribute('data-sia-pack'));
    const pack = DATA.packs[i];
    const on = i === state.pack;
    card.classList.toggle('is-selected', on);
    const head = $('[data-sia-pack-select]', card);
    if (head) head.setAttribute('aria-checked', on ? 'true' : 'false');
    if (!pack) return;
    const price = packPrice(pack, v), compare = packCompare(pack, v);
    const pe = $('[data-sia-pack-price]', card); if (pe) pe.textContent = fmt(price);
    const we = $('[data-sia-pack-was]', card); if (we) we.textContent = fmt(compare);
    const se = $('[data-sia-pack-save]', card);
    if (se && compare > price) se.textContent = '−' + Math.round((compare - price) / compare * 100) + ' %';
  });
  $$('.sia-swatch').forEach((sw) => {
    const on = Number(sw.getAttribute('data-sia-variant')) === v.id;
    sw.classList.toggle('is-active', on);
    sw.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
  $$('[data-sia-color-name]').forEach((el) => { el.textContent = v.title; });
  $$('[data-sia-toggle]').forEach((t) => {
    const k = t.getAttribute('data-sia-toggle');
    t.setAttribute('aria-checked', state[k] ? 'true' : 'false');
  });
  const up = $('[data-sia-unit-price]'); if (up) up.textContent = fmt(v.price);
  const uc = $('[data-sia-unit-compare]'); if (uc) uc.textContent = fmt(v.compare);
  const pack = DATA.packs[state.pack];
  $$('[data-sia-atc-total]').forEach((el) => { el.textContent = fmt(atcTotal()); });
  $$('[data-sia-pack-label]').forEach((el) => { el.textContent = pack ? pack.sub || unitLabel(pack.qty) : ''; });
  const stickyImg = $('.sia-sticky img');
  if (stickyImg && v.img) { stickyImg.src = v.img; stickyImg.removeAttribute('srcset'); }
}

/* ---------- Gallery ---------- */
const slides = $$('[data-sia-slide]');
const thumbs = $$('[data-sia-thumb]');
let slideIndex = 0;
function showSlide(i) {
  if (!slides.length) return;
  slideIndex = (i + slides.length) % slides.length;
  slides.forEach((img, n) => img.classList.toggle('is-active', n === slideIndex));
  thumbs.forEach((t, n) => {
    const on = n === slideIndex;
    t.classList.toggle('is-active', on);
    if (on) {
      const row = t.parentElement;
      const left = t.offsetLeft - row.offsetLeft;
      if (left < row.scrollLeft || left + t.offsetWidth > row.scrollLeft + row.clientWidth) {
        row.scrollTo({ left: left - row.clientWidth / 2 + t.offsetWidth / 2, behavior: 'smooth' });
      }
    }
  });
}
thumbs.forEach((t) => t.addEventListener('click', () => showSlide(Number(t.getAttribute('data-sia-thumb')))));
$$('[data-sia-prev]').forEach((b) => b.addEventListener('click', () => showSlide(slideIndex - 1)));
$$('[data-sia-next]').forEach((b) => b.addEventListener('click', () => showSlide(slideIndex + 1)));

/* ---------- Packs, colours, add-ons ---------- */
$$('[data-sia-pack-select]').forEach((b) => b.addEventListener('click', () => {
  state.pack = Number(b.getAttribute('data-sia-pack-select'));
  renderProduct();
}));
$$('.sia-swatch').forEach((sw) => sw.addEventListener('click', () => {
  const id = Number(sw.getAttribute('data-sia-variant'));
  const v = DATA.variants.find((x) => x.id === id);
  if (!v) return;
  state.variant = v;
  const idx = slides.findIndex((s) => Number(s.getAttribute('data-media-id')) === v.mediaId);
  if (idx > -1) showSlide(idx);
  renderProduct();
}));
$$('[data-sia-toggle]').forEach((t) => t.addEventListener('click', () => {
  const k = t.getAttribute('data-sia-toggle');
  state[k] = !state[k];
  renderProduct();
}));

/* ---------- Delivery timeline ---------- */
const tl = $('[data-sia-timeline]');
if (tl) {
  const shipD = Number(tl.dataset.shipDays) || 0, minD = Number(tl.dataset.minDays) || 6, maxD = Number(tl.dataset.maxDays) || 10;
  const dates = [shortDate(addDays(0)), shortDate(addDays(shipD)), shortDate(addDays(minD)) + ' – ' + shortDate(addDays(maxD))];
  $$('[data-sia-date]', tl).forEach((el) => { el.textContent = dates[Number(el.getAttribute('data-sia-date'))]; });
}

/* ====================== Cart helpers ====================== */
async function getCart() { return (await fetch(window.Shopify && Shopify.routes ? Shopify.routes.root + 'cart.js' : '/cart.js', { headers: { Accept: 'application/json' } })).json(); }
function route(path) { return (window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + path; }

/** POST to a cart endpoint, keeping the theme's cart state in sync (D8). */
async function cartPost(path, body, action, lines) {
  const deferred = CartLinesUpdateEvent.createPromise();
  try {
    document.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'product', lines: lines || [], promise: deferred.promise }));
  } catch (e) { /* the theme may not listen on this page - harmless */ }
  try {
    const res = await fetch(route(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.description || json.message || 'Erreur');
    const cart = await getCart();
    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: false, source: 'siestair' } });
    return cart;
  } catch (err) {
    deferred.resolve({ cart: null, detail: { didError: true } });
    throw err;
  }
}

/* ====================== Add to cart ====================== */
const errorEl = $('[data-sia-error]');
async function addToCart(btn) {
  if (!DATA) return;
  const buttons = $$('[data-sia-atc]');
  buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
  if (errorEl) errorEl.hidden = true;
  try {
    const current = await getCart();
    const inCart = (id) => current.items.some((it) => it.variant_id === id);
    const pack = DATA.packs[state.pack];
    const items = [{ id: state.variant.id, quantity: pack.qty }];
    if (state.ship && DATA.ship && !inCart(DATA.ship.id)) items.push({ id: DATA.ship.id, quantity: 1 });
    if (state.warr && DATA.warr && !inCart(DATA.warr.id)) items.push({ id: DATA.warr.id, quantity: 1 });
    const lines = items.map((i) => ({ merchandiseId: 'gid://shopify/ProductVariant/' + i.id, quantity: i.quantity }));
    const cart = await cartPost('cart/add.js', { items }, 'add', lines);
    startReserve();
    await openDrawer(cart);
  } catch (err) {
    if (errorEl) { errorEl.textContent = err.message || 'Impossible d’ajouter au panier.'; errorEl.hidden = false; }
  } finally {
    buttons.forEach((b) => b.removeAttribute('aria-busy'));
  }
}
$$('[data-sia-atc]').forEach((b) => b.addEventListener('click', () => addToCart(b)));

/* ====================== Drawer ====================== */
const drawer = $('[data-sia-drawer]');
const D = drawer ? drawer.dataset : {};
const RESERVE_KEY = 'sia-reserve-end';
let lastFocus = null;
let syncing = false;
let tick = null;

function reserveEnd() {
  try { return Number(sessionStorage.getItem(RESERVE_KEY)) || 0; } catch (e) { return 0; }
}
function startReserve() {
  if (reserveEnd()) return; // never reset on further adds
  const mins = Number(D.reserveMinutes) || 5;
  try { sessionStorage.setItem(RESERVE_KEY, String(Date.now() + mins * 60000)); } catch (e) { /* private mode */ }
}
function renderTimer(hasItems) {
  const box = $('[data-sia-timer]', drawer);
  if (!box) return;
  const end = reserveEnd();
  if (!end || !hasItems) { box.hidden = true; return; }
  box.hidden = false;
  const left = Math.max(0, end - Date.now());
  const sec = Math.ceil(left / 1000);
  const expired = left <= 0;
  $('[data-sia-timer-time]', box).textContent = pad(Math.floor(sec / 60)) + ':' + pad(sec % 60);
  $('[data-sia-timer-msg]', box).textContent = expired ? D.msgExpired : sec < 60 ? D.msgUrgent : D.msgOk;
  box.classList.toggle('is-urgent', expired || sec < 60);
}

function variantInfo(id) { return DATA ? DATA.variants.find((v) => v.id === id) : null; }
function isMain(item) { return DATA && item.product_id === DATA.productId; }
function isAddon(item) { return DATA && ((DATA.ship && item.variant_id === DATA.ship.id) || (DATA.warr && item.variant_id === DATA.warr.id)); }
function isGift(item) { return DATA && DATA.gift && item.variant_id === DATA.gift.id; }
function imgUrl(src, w) {
  if (!src) return '';
  return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
}

function el(tag, cls, text) {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
}

function mainLine(item, index) {
  const row = el('div', 'sia-line');
  const img = el('img'); img.src = imgUrl(item.image, 120); img.alt = ''; img.width = 44; img.height = 44; img.loading = 'lazy';
  const mid = el('div', 'sia-line-mid');
  let label = item.product_title;
  if (isMain(item)) {
    const pack = packForQty(item.quantity);
    label = DATA.productTitle + ' — ' + (pack ? pack.name + ' (' + unitLabel(item.quantity) + ')' : unitLabel(item.quantity)) + (item.variant_title ? ' · ' + item.variant_title : '');
  } else if (item.variant_title) {
    label += ' · ' + item.variant_title;
  }
  mid.appendChild(el('strong', null, label));
  const step = el('div', 'sia-stepper');
  const dec = el('button', null, '−'); dec.type = 'button'; dec.setAttribute('aria-label', 'Retirer un');
  const qty = el('span', null, String(item.quantity));
  const inc = el('button', null, '+'); inc.type = 'button'; inc.setAttribute('aria-label', 'Ajouter un');
  dec.addEventListener('click', () => changeLine(item.key, item.quantity - 1));
  inc.addEventListener('click', () => changeLine(item.key, item.quantity + 1));
  step.append(dec, qty, inc);
  mid.appendChild(step);
  const end = el('div', 'sia-line-end');
  end.appendChild(el('strong', null, fmt(item.final_line_price)));
  const info = variantInfo(item.variant_id);
  const compare = info ? info.compare * item.quantity : item.original_line_price;
  if (compare > item.final_line_price) end.appendChild(el('s', null, fmt(compare)));
  row.append(img, mid, end);
  return row;
}

function addonLine(item, label, removable) {
  const row = el('div', 'sia-addon-line');
  row.appendChild(el('span', null, removable ? '✓' : '🎁'));
  row.appendChild(el('span', null, label));
  const price = el('strong', item.final_line_price ? '' : 'is-free', item.final_line_price ? fmt(item.final_line_price) : DATA.freeLabel);
  row.appendChild(price);
  if (removable) {
    const x = el('button', null, '×'); x.type = 'button'; x.setAttribute('aria-label', 'Retirer ' + label);
    x.addEventListener('click', () => changeLine(item.key, 0));
    row.appendChild(x);
  }
  return row;
}

async function changeLine(key, quantity) {
  const panel = $('.sia-panel', drawer);
  panel.classList.add('sia-d-busy');
  try {
    const cart = await cartPost('cart/change.js', { id: key, quantity: Math.max(0, quantity) }, 'change');
    await renderCart(cart);
  } catch (e) { /* keep the drawer as is */ } finally {
    panel.classList.remove('sia-d-busy');
  }
}

async function syncGift(cart) {
  if (!DATA || !DATA.gift || syncing) return cart;
  const hasMain = cart.items.some(isMain);
  const giftItem = cart.items.find(isGift);
  const want = hasMain && cart.total_price >= DATA.gift.threshold;
  try {
    if (want && !giftItem) {
      syncing = true;
      return await cartPost('cart/add.js', { items: [{ id: DATA.gift.id, quantity: 1 }] }, 'add');
    }
    if (!want && giftItem) {
      syncing = true;
      return await cartPost('cart/change.js', { id: giftItem.key, quantity: 0 }, 'change');
    }
    if (giftItem && giftItem.quantity > 1) {
      syncing = true;
      return await cartPost('cart/change.js', { id: giftItem.key, quantity: 1 }, 'change');
    }
  } catch (e) { /* gift is a bonus - never block the cart */ } finally {
    syncing = false;
  }
  return cart;
}

async function renderCart(cartIn) {
  if (!drawer) return;
  const cart = await syncGift(cartIn || await getCart());
  const items = cart.items;
  const hasItems = items.length > 0;
  $$('[data-sia-cart-count]').forEach((n) => { n.textContent = String(cart.item_count); });
  $('[data-sia-empty]', drawer).hidden = hasItems;
  $('[data-sia-foot]', drawer).hidden = !hasItems;

  const wrap = $('[data-sia-lines]', drawer);
  wrap.textContent = '';
  items.forEach((it, i) => { if (!isAddon(it) && !isGift(it)) wrap.appendChild(mainLine(it, i)); });
  items.forEach((it) => {
    if (DATA && DATA.ship && it.variant_id === DATA.ship.id) wrap.appendChild(addonLine(it, DATA.ship.label, true));
    if (DATA && DATA.warr && it.variant_id === DATA.warr.id) wrap.appendChild(addonLine(it, DATA.warr.label, true));
  });
  items.forEach((it) => { if (isGift(it)) wrap.appendChild(addonLine(it, DATA.gift.label, false)); });

  // Free-gift progress
  const giftBox = $('[data-sia-gift]', drawer);
  if (DATA && DATA.gift && hasItems) {
    const net = cart.total_price;
    const done = net >= DATA.gift.threshold;
    giftBox.hidden = false;
    $('[data-sia-gift-msg]', giftBox).textContent = done ? D.giftDone : fill(D.giftLeft, { montant: fmt(DATA.gift.threshold - net) });
    $('[data-sia-gift-bar]', giftBox).style.width = Math.min(100, Math.round(net / DATA.gift.threshold * 100)) + '%';
  } else if (giftBox) {
    giftBox.hidden = true;
  }

  // Upsell: next pack up for the SiestAir quantity in the cart
  const up = $('[data-sia-upsell]', drawer);
  up.hidden = true;
  if (DATA) {
    const mains = items.filter(isMain);
    const total = mains.reduce((a, it) => a + it.quantity, 0);
    const next = DATA.packs.filter((p) => p.qty > total).sort((a, b) => a.qty - b.qty)[0];
    if (mains.length && next && total > 0) {
      const v = variantInfo(mains[0].variant_id) || state.variant;
      const curPrice = priceForQty(total, v);
      const nextPrice = packPrice(next, v);
      const nextCompare = packCompare(next, v);
      const pct = nextCompare > nextPrice ? '−' + Math.round((nextCompare - nextPrice) / nextCompare * 100) + ' %' : '';
      $('[data-sia-upsell-title]', up).textContent = fill(D.upsellTitle, { pack: next.name, remise: pct }).replace(' ()', '');
      $('[data-sia-upsell-sub]', up).textContent = fill(D.upsellSub, { ecart: fmt(nextPrice - curPrice), unite: fmt(Math.round(nextPrice / next.qty)) });
      const uimg = $('[data-sia-upsell-img]', up);
      uimg.src = imgUrl(mains[0].image, 80);
      const btn = $('[data-sia-upsell-btn]', up);
      btn.onclick = () => changeLine(mains[0].key, mains[0].quantity + (next.qty - total));
      up.hidden = false;
    }
  }

  // Totals
  let compareTotal = Number(D.shipWas) || 0;
  items.forEach((it) => {
    const info = variantInfo(it.variant_id);
    if (info) compareTotal += Math.max(info.compare * it.quantity, it.final_line_price);
    else if (isGift(it)) compareTotal += (DATA.gift.compare || 0) * it.quantity;
    else compareTotal += Math.max(it.original_line_price, it.final_line_price);
  });
  const total = cart.total_price;
  $$('[data-sia-total]', drawer).forEach((n) => { n.textContent = fmt(total); });
  const ct = $('[data-sia-compare-total]', drawer);
  ct.textContent = compareTotal > total ? fmt(compareTotal) : '';
  const sv = $('[data-sia-savings]', drawer);
  sv.hidden = !(compareTotal > total);
  sv.textContent = fill(D.saveLabel, { montant: fmt(compareTotal - total) });

  // Applied promo codes
  const msg = $('[data-sia-promo-msg]', drawer);
  const codes = (cart.discount_codes || []).filter((c) => c.applicable);
  if (codes.length) {
    msg.hidden = false; msg.classList.remove('is-error');
    msg.textContent = fill(D.promoOk, { code: codes.map((c) => c.code).join(', ') });
  }

  renderTimer(hasItems);
  return cart;
}

async function openDrawer(cart) {
  if (!drawer) return;
  lastFocus = document.activeElement;
  await renderCart(cart);
  drawer.classList.add('is-open');
  drawer.setAttribute('aria-hidden', 'false');
  document.documentElement.style.overflow = 'hidden';
  setSticky(false);
  const panel = $('.sia-panel', drawer);
  panel.focus({ preventScroll: true });
  clearInterval(tick);
  tick = setInterval(() => renderTimer(!$('[data-sia-foot]', drawer).hidden), 1000);
}
function closeDrawer() {
  if (!drawer) return;
  drawer.classList.remove('is-open');
  drawer.setAttribute('aria-hidden', 'true');
  document.documentElement.style.overflow = '';
  clearInterval(tick);
  if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  updateSticky();
}

if (drawer) {
  $$('[data-sia-close]', drawer).forEach((b) => b.addEventListener('click', closeDrawer));
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer(); });
  // Header button, and the theme's own cart triggers, open THIS drawer (one cart UI per page).
  document.addEventListener('click', (e) => {
    const t = e.target.closest && e.target.closest('[data-sia-open-cart], [data-testid="cart-drawer-trigger"], a[href$="/cart"]');
    if (!t || drawer.contains(t)) return;
    e.preventDefault();
    e.stopPropagation();
    openDrawer();
  }, true);

  // Promo code -> real cart discount, so checkout receives it
  const toggle = $('[data-sia-promo-toggle]', drawer);
  const form = $('[data-sia-promo]', drawer);
  toggle.addEventListener('click', () => {
    form.hidden = !form.hidden;
    toggle.setAttribute('aria-expanded', form.hidden ? 'false' : 'true');
    if (!form.hidden) $('[data-sia-promo-input]', form).focus();
  });
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const input = $('[data-sia-promo-input]', form);
    const code = input.value.trim();
    if (!code) return;
    const msg = $('[data-sia-promo-msg]', drawer);
    try {
      const existing = ((await getCart()).discount_codes || []).filter((c) => c.applicable).map((c) => c.code);
      let cart = await cartPost('cart/update.js', { discount: existing.concat(code).join(',') }, 'update');
      const hit = (cart.discount_codes || []).find((c) => c.code.toLowerCase() === code.toLowerCase());
      if (!hit || !hit.applicable) {
        cart = await cartPost('cart/update.js', { discount: existing.join(',') }, 'update');
        msg.hidden = false; msg.classList.add('is-error'); msg.textContent = D.promoBad;
        await renderCart(cart);
        msg.hidden = false; msg.classList.add('is-error'); msg.textContent = D.promoBad;
        return;
      }
      input.value = '';
      await renderCart(cart);
    } catch (err) {
      msg.hidden = false; msg.classList.add('is-error'); msg.textContent = D.promoBad;
    }
  });

  const eta = $('[data-sia-eta]', drawer);
  if (eta) eta.textContent = shortDate(addDays(Number(D.etaMin) || 6)) + ' – ' + shortDate(addDays(Number(D.etaMax) || 10));
}

/* ====================== Sticky bar ====================== */
const sticky = $('[data-sia-sticky]');
const mainAtc = $('[data-sia-main-atc]');
let pastAtc = false;
function setSticky(on) {
  if (!sticky) return;
  sticky.classList.toggle('is-visible', on);
  sticky.setAttribute('aria-hidden', on ? 'false' : 'true');
  $$('[data-sia-atc]', sticky).forEach((b) => { b.tabIndex = on ? 0 : -1; });
}
function updateSticky() { setSticky(pastAtc && !(drawer && drawer.classList.contains('is-open'))); }
if (sticky && mainAtc) {
  const onScroll = () => {
    const past = mainAtc.getBoundingClientRect().bottom < 0;
    if (past !== pastAtc) { pastAtc = past; updateSticky(); }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  onScroll();
}

/* ====================== Reviews ====================== */
const reviews = $('[data-sia-reviews]');
if (reviews) {
  const more = $('[data-sia-more]', reviews);
  const step = Number(reviews.dataset.step) || 6;
  if (more) more.addEventListener('click', () => {
    const hidden = $$('[data-sia-more-item][hidden]', reviews);
    hidden.slice(0, step).forEach((r) => { r.hidden = false; });
    if (hidden.length <= step) more.hidden = true;
  });
  const write = $('[data-sia-write]', reviews);
  const wrap = $('[data-sia-form-wrap]', reviews);
  if (write && wrap) {
    write.addEventListener('click', () => {
      wrap.hidden = !wrap.hidden;
      write.setAttribute('aria-expanded', wrap.hidden ? 'false' : 'true');
      if (!wrap.hidden) { const f = $('input[type="text"]', wrap); if (f) f.focus(); }
    });
    if ($('.sia-form-msg, .sia-error', wrap) || /contact_posted/.test(location.search)) {
      wrap.hidden = false;
      write.setAttribute('aria-expanded', 'true');
    }
  }
}

/* ====================== FAQ ====================== */
$$('[data-sia-faq]').forEach((faq) => {
  const qs = $$('[data-sia-faq-q]', faq);
  qs.forEach((q) => q.addEventListener('click', () => {
    const open = q.getAttribute('aria-expanded') === 'true';
    qs.forEach((o) => {
      o.setAttribute('aria-expanded', 'false');
      o.lastElementChild.textContent = '+';
      document.getElementById(o.getAttribute('aria-controls')).hidden = true;
    });
    if (!open) {
      q.setAttribute('aria-expanded', 'true');
      q.lastElementChild.textContent = '−';
      document.getElementById(q.getAttribute('aria-controls')).hidden = false;
    }
  }));
});

/* ====================== Videos ====================== */
$$('[data-sia-videos]').forEach((sec) => {
  const row = $('[data-sia-row]', sec);
  $$('[data-sia-scroll]', sec).forEach((b) => b.addEventListener('click', () => {
    row.scrollBy({ left: Number(b.getAttribute('data-sia-scroll')) * Math.max(200, row.clientWidth * 0.6), behavior: 'smooth' });
  }));
  $$('video', row).forEach((v) => {
    v.muted = true;
    const p = v.play(); if (p && p.catch) p.catch(() => {});
  });
});

renderProduct();
